import React from 'react';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client';
import { GridCell, Grid, Page } from 'styles';
import { copyFor } from 'config';
import { ContactSupport } from 'components/modules/contact-support';
import { PageHeading } from 'components/page-heading';
import { BillingStatusData } from 'hooks/use-billing-status.hook';
import { formatAsMoney } from '@perch-labs/warbler';
import { Card, Link, Text } from '@perch-labs/shrike';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BillingExplainer } from './components/billing-explainer';
import { LatestStatement } from './components/latest-statement';
import { BillHistory } from './components/bill-history';

interface Props {
  billPayCta?: React.ReactNode;
  error?: ApolloError;
  issuedStatements: BillingStatusData['issuedStatements'];
  loading: boolean;
  mostRecentStatement: BillingStatusData['mostRecentStatement'];
  balances?: BillingStatusData['balances'];
  paymentLink?: BillingStatusData['paymentLink'];
}

const getCopy = copyFor('billing');

export const BillingBase = (props: Props) => {
  const {
    billPayCta,
    error,
    issuedStatements,
    loading,
    mostRecentStatement,
    balances,
    paymentLink,
  } = props;

  const flags = useFlags();

  const pastDueCents = balances?.pastDueCents ?? 0;
  const balanceDueCents = balances?.balanceCents ?? 0;
  const paymentUrl = paymentLink?.stripeCheckoutUrl;

  const StyledCard = styled(Card)<{ isPastDue: boolean }>`
    background-color: ${props =>
      props.isPastDue ? props.theme.colors.background.error : 'white'};
    border: 1px solid;
  `;

  return (
    <Page small={{ padding: '0 0 24px' }}>
      <PageHeading page={getCopy('heading')} />
      <Grid width="100%" gap="24px">
        {balanceDueCents > 0 && flags.showPlanBalanceBanner && (
          <GridCell columns={12} small={{ columns: 8 }} borderRadius="8px">
            <StyledCard isPastDue={pastDueCents > 0}>
              <div style={{ padding: '16px' }}>
                <Grid width="100%">
                  <GridCell
                    columns={10}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Text textStyle="heading600">
                      {pastDueCents > 0
                        ? `Your account balance is ${formatAsMoney(
                            balanceDueCents,
                            {
                              conversion: 'fromCents',
                            }
                          )}, of which ${formatAsMoney(pastDueCents, {
                            conversion: 'fromCents',
                          })} is past due.`
                        : `There is a balance due on your account in the amount of ${formatAsMoney(
                            balanceDueCents,
                            {
                              conversion: 'fromCents',
                            }
                          )}.`}
                    </Text>
                    <Text>
                      Please pay your balance at your earliest convenience.
                    </Text>
                  </GridCell>
                  <GridCell
                    columns={2}
                    align="end"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    {paymentUrl && (
                      <Link href={paymentUrl}>Make a Payment</Link>
                    )}
                  </GridCell>
                </Grid>
              </div>
            </StyledCard>
          </GridCell>
        )}
        <GridCell rows={4} columns={5} small={{ columnStart: 1, columns: 8 }}>
          <LatestStatement
            error={error}
            loading={loading}
            mostRecentStatement={mostRecentStatement?.data}
          />
        </GridCell>
        <GridCell columns={7} small={{ columnStart: 1, columns: 8 }}>
          <BillHistory
            statements={issuedStatements}
            error={error}
            loading={loading}
          />
        </GridCell>
        <GridCell
          columnStart={6}
          columns={7}
          small={{ columnStart: 1, columns: 8 }}
        >
          {billPayCta}
          <BillingExplainer accordion />
          <ContactSupport margin={{ top: '24px' }} />
        </GridCell>
      </Grid>
    </Page>
  );
};
